import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { Boxes, ChevronRight, RefreshCw, Download, Loader2, AlertTriangle, Filter, X } from 'lucide-react';
import CustomDatePicker from '../../components/CustomDatePicker';

import {
    fetchStockStatusCCList, fetchStockStatusReport, fetchStockStatusPopup,
    clearStockStatusReport, clearStockStatusPopup,
    selectStockStatusCCList, selectStockStatusCCListLoading,
    selectStockStatusReport, selectStockStatusReportLoading, selectStockStatusReportError,
    selectStockStatusPopupData, selectStockStatusPopupLoading,
} from '../../slices/ReportsSlice/stockStatusReportSlice';

const inputCls = 'w-full px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent';

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** CustomDatePicker's onChange always hands back a Date object (not a formatted string) */
const toDDMonYYYY = (val) => {
    if (!val) return '';
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

const AMOUNT_KEYS = ['BasicPrice', 'AmountAgainstTotalRecievedCC', 'AmtOfConsumedItemsAtCC', 'AmtAgainstTotalTransferedToOtherCC', 'AmtOfBalanceStockAtCC', 'AmtOfLostDamage'];

const NumCell = ({ value }) => (
    <td className="px-2 py-1 text-xs text-right text-gray-700 dark:text-gray-200 whitespace-nowrap">
        {parseFloat(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </td>
);

const PopupModal = ({ itemCode, rows, loading, onClose }) => {
    const totals = useMemo(() => rows.reduce((acc, r) => {
        acc.RecievedQuantity += parseFloat(r.RecievedQuantity) || 0;
        acc.IssuedQuantity += parseFloat(r.IssuedQuantity) || 0;
        acc.BalanceQuantity += parseFloat(r.BalanceQuantity) || 0;
        return acc;
    }, { RecievedQuantity: 0, IssuedQuantity: 0, BalanceQuantity: 0 }), [rows]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-y-auto">
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-indigo-600 to-violet-600 rounded-t-2xl">
                    <h2 className="text-sm font-bold text-white">Item Movement — {itemCode}</h2>
                    <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"><X className="w-4 h-4 text-white" /></button>
                </div>
                <div className="p-4">
                    {loading ? (
                        <div className="flex items-center justify-center gap-3 py-10"><Loader2 className="h-5 w-5 animate-spin text-indigo-600" /><span className="text-sm text-gray-500">Loading…</span></div>
                    ) : rows.length === 0 ? (
                        <p className="text-center text-sm text-gray-400 py-8">No transactions found.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-[11px] divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-100 dark:bg-gray-900/40">
                                    <tr>
                                        {['Date', 'CC Code', 'Particular', 'Mrr/Refno', 'Rate', 'Received Qty', 'Issue Qty', 'Balance Qty'].map((h) => (
                                            <th key={h} className="px-2 py-1.5 text-left font-bold text-gray-500 uppercase whitespace-nowrap">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                    {rows.map((r, i) => (
                                        <tr key={i} className="hover:bg-indigo-50 dark:hover:bg-indigo-900/10">
                                            <td className="px-2 py-1 whitespace-nowrap">{r.Date}</td>
                                            <td className="px-2 py-1 whitespace-nowrap">{r.CCCode}</td>
                                            <td className="px-2 py-1">{r.Particular}</td>
                                            <td className="px-2 py-1 whitespace-nowrap">{r.MrrorRefno}</td>
                                            <td className="px-2 py-1 text-right">{r.Rate}</td>
                                            <td className="px-2 py-1 text-right">{parseFloat(r.RecievedQuantity || 0).toFixed(4)}</td>
                                            <td className="px-2 py-1 text-right">{parseFloat(r.IssuedQuantity || 0).toFixed(4)}</td>
                                            <td className="px-2 py-1 text-right font-semibold">{parseFloat(r.BalanceQuantity || 0).toFixed(4)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="bg-gray-100 dark:bg-gray-900/50 font-bold">
                                        <td className="px-2 py-1" colSpan={5}>TOTAL</td>
                                        <td className="px-2 py-1 text-right">{totals.RecievedQuantity.toFixed(4)}</td>
                                        <td className="px-2 py-1 text-right">{totals.IssuedQuantity.toFixed(4)}</td>
                                        <td className="px-2 py-1 text-right">{totals.BalanceQuantity.toFixed(4)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const StockStatusReport = () => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID;
    const uid = userData?.UID || userData?.uid;

    const ccList = useSelector(selectStockStatusCCList);
    const ccListLoading = useSelector(selectStockStatusCCListLoading);
    const report = useSelector(selectStockStatusReport);
    const reportLoading = useSelector(selectStockStatusReportLoading);
    const reportError = useSelector(selectStockStatusReportError);
    const popupData = useSelector(selectStockStatusPopupData);
    const popupLoading = useSelector(selectStockStatusPopupLoading);

    const [storeStatus, setStoreStatus] = useState('');
    const [ccCode, setCcCode] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [ranParams, setRanParams] = useState(null);
    const [popupItem, setPopupItem] = useState(null);

    useEffect(() => {
        setCcCode('');
        setFromDate(''); setToDate('');
        if (storeStatus && roleId && uid) {
            dispatch(fetchStockStatusCCList({ UID: uid, RID: roleId, StoreStatus: storeStatus }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeStatus]);

    const handleFromDateChange = (val) => {
        setFromDate(val);
        if (!(val instanceof Date) || isNaN(val.getTime())) return;
        const suggested = new Date(val.getFullYear(), val.getMonth() + 12, 0); // last day, 11 months after
        const today = new Date();
        setToDate(suggested > today ? today : suggested);
    };

    const handleReset = () => {
        setStoreStatus(''); setCcCode(''); setFromDate(''); setToDate(''); setRanParams(null);
        dispatch(clearStockStatusReport());
    };

    const runReport = () => {
        if (!storeStatus) { toast.warning('Select a Cost Centre Status.'); return; }
        if (!ccCode) { toast.warning('Select a Cost Centre.'); return; }
        if (!fromDate) { toast.warning('Select a From Date.'); return; }
        if (!toDate) { toast.warning('Select a To Date.'); return; }
        const params = { CCCode: ccCode, Fromdate: toDDMonYYYY(fromDate), Todate: toDDMonYYYY(toDate) };
        setRanParams(params);
        dispatch(fetchStockStatusReport(params));
    };

    const handleRefresh = () => { if (ranParams) dispatch(fetchStockStatusReport(ranParams)); };

    const rows = report?.StockList || [];

    const totals = useMemo(() => rows.reduce((acc, r) => {
        AMOUNT_KEYS.forEach((k) => { acc[k] = (acc[k] || 0) + (parseFloat(r[k]) || 0); });
        return acc;
    }, {}), [rows]);

    const openPopup = (itemCode) => {
        if (!itemCode) return;
        setPopupItem(itemCode);
        dispatch(fetchStockStatusPopup({ Itemcode: itemCode, CCcode: ranParams.CCCode, FromDate: ranParams.Fromdate, ToDate: ranParams.Todate }));
    };
    const closePopup = () => { setPopupItem(null); dispatch(clearStockStatusPopup()); };

    const handleExport = () => {
        if (rows.length === 0) { toast.warning('No data to export'); return; }
        const exportRows = [...rows, { ItemCode: '', ItemName: 'TOTAL', ...totals }].map((r) => ({
            'Item Code': r.ItemCode, 'Item Name': r.ItemName, Specification: r.Specification, Units: r.Units,
            'Opening Stock': r.OpeningStock,
            'Received From Central Store': r.rcv_frm_central, 'Received From New Stock': r.rcv_frm_newstock, 'Purchase At CC': r.purchased_at_cc, 'Total Received At CC': r.TotalRecievedAtCC,
            'Transfer To Central Store': r.Trans_to_central, 'Transfer To New Stock': r.Trans_to_newstock, 'Issued For CC Consumption': r.Issue_for_cons, 'Total Out From CC': r.TotalOutFromCC,
            'Current Stock At CC': r.BalanceStockAtCC, 'Lost/Damaged Accepted': r.lostdamaged_rep_accep,
            'Basic Price': r.BasicPrice, 'Amount Against Total Received': r.AmountAgainstTotalRecievedCC, 'Amt Of Consumed Items At CC': r.AmtOfConsumedItemsAtCC,
            'Amt Against Total Transferred To Other CC': r.AmtAgainstTotalTransferedToOtherCC, 'Amt Of Balance Stock At CC': r.AmtOfBalanceStockAtCC, 'Amt Of Lost & Damage': r.AmtOfLostDamage,
            'Closing Stock': r.ClosingStock,
        }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(exportRows), 'Stock Status');
        XLSX.writeFile(wb, `Stock_Status_Report_${ranParams?.CCCode || ''}_${new Date().toISOString().slice(0, 10)}.xlsx`);
        toast.success('Excel downloaded');
    };

    return (
        <div className="space-y-3 p-4 text-xs">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <div className="p-1.5 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-lg">
                                <Boxes className="h-4 w-4 text-white" />
                            </div>
                            Stock Status Report
                        </h1>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Item-wise receipt, issue and closing stock status for a cost centre</p>
                        <nav className="flex items-center gap-1.5 text-[11px] text-gray-400 mt-1">
                            <span>Reports</span><ChevronRight className="w-3 h-3" /><span className="text-gray-700 dark:text-gray-200">Stock Status Report</span>
                        </nav>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={handleRefresh} disabled={reportLoading || !ranParams} className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-60">
                            <RefreshCw className={`h-3.5 w-3.5 ${reportLoading ? 'animate-spin' : ''}`} />Refresh
                        </button>
                        <button onClick={handleExport} disabled={rows.length === 0} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-60">
                            <Download className="h-3.5 w-3.5" />Export Excel
                        </button>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-1.5 mb-2">
                    <Filter className="h-3.5 w-3.5 text-indigo-600" />
                    <h2 className="text-xs font-semibold text-gray-700 dark:text-gray-300">Filters</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Cost Centre Status</label>
                        <select className={inputCls} value={storeStatus} onChange={(e) => setStoreStatus(e.target.value)}>
                            <option value="">-Please Select-</option>
                            <option value="Active">Active</option>
                            <option value="Closed">Closed</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Cost Centre</label>
                        <select className={inputCls} value={ccCode} onChange={(e) => setCcCode(e.target.value)} disabled={!storeStatus || ccListLoading}>
                            <option value="">-Please Select-</option>
                            {ccList.map((c) => <option key={c.CCID} value={c.CCID}>{c.CCVAL}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">From Date</label>
                        <CustomDatePicker value={fromDate} onChange={handleFromDateChange} maxDate={new Date()} />
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">To Date</label>
                        <CustomDatePicker value={toDate} onChange={setToDate} maxDate={new Date()} />
                    </div>
                    <div className="flex gap-2">
                        <button onClick={runReport} disabled={reportLoading} className="flex-1 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-60">View</button>
                        <button onClick={handleReset} className="flex-1 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-xs font-semibold transition-colors">Reset</button>
                    </div>
                </div>
            </div>

            {/* Report */}
            {reportLoading ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-10">
                    <Loader2 className="h-6 w-6 animate-spin text-indigo-600" /><span className="text-xs text-gray-500 dark:text-gray-400">Loading report…</span>
                </div>
            ) : reportError ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-10 text-red-500">
                    <AlertTriangle className="h-5 w-5" /><span className="text-xs">{reportError}</span>
                </div>
            ) : ranParams && rows.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 py-10 text-center text-xs text-gray-400">No data found.</div>
            ) : rows.length > 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                        <h2 className="text-xs font-bold text-gray-700 dark:text-gray-300">Item-wise Stock Status</h2>
                    </div>
                    <div className="overflow-auto max-h-[70vh]">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gradient-to-r from-indigo-600 to-violet-600 sticky top-0 z-10">
                                <tr>
                                    <th rowSpan={2} className="px-2 py-1 text-left text-[10px] font-bold text-white uppercase align-bottom">Item Code</th>
                                    <th rowSpan={2} className="px-2 py-1 text-left text-[10px] font-bold text-white uppercase align-bottom w-[180px]">Item Name</th>
                                    <th rowSpan={2} className="px-2 py-1 text-left text-[10px] font-bold text-white uppercase align-bottom w-[160px]">Specification</th>
                                    <th rowSpan={2} className="px-2 py-1 text-left text-[10px] font-bold text-white uppercase align-bottom">Units</th>
                                    <th rowSpan={2} className="px-2 py-1 text-center text-[10px] font-bold text-white uppercase align-bottom">Opening Stock</th>
                                    <th colSpan={4} className="px-2 py-1 text-center text-[10px] font-bold text-white uppercase border-l border-indigo-400">Total Receipt At CC</th>
                                    <th colSpan={4} className="px-2 py-1 text-center text-[10px] font-bold text-white uppercase border-l border-indigo-400">Total Out From CC</th>
                                    <th rowSpan={2} className="px-2 py-1 text-center text-[10px] font-bold text-white uppercase border-l border-indigo-400 align-bottom">Current Stock At CC</th>
                                    <th rowSpan={2} className="px-2 py-1 text-center text-[10px] font-bold text-white uppercase align-bottom">Lost/Damaged Accepted</th>
                                    <th colSpan={6} className="px-2 py-1 text-center text-[10px] font-bold text-white uppercase border-l border-indigo-400">Amount Details</th>
                                    <th rowSpan={2} className="px-2 py-1 text-center text-[10px] font-bold text-white uppercase border-l border-indigo-400 align-bottom">Periodically Stock</th>
                                </tr>
                                <tr className="bg-indigo-700/60">
                                    <th className="px-2 py-1 text-center text-[10px] font-semibold text-indigo-100 whitespace-nowrap">Recd. Central Store</th>
                                    <th className="px-2 py-1 text-center text-[10px] font-semibold text-indigo-100 whitespace-nowrap">Recd. New Stock</th>
                                    <th className="px-2 py-1 text-center text-[10px] font-semibold text-indigo-100 whitespace-nowrap">Purchase At CC</th>
                                    <th className="px-2 py-1 text-center text-[10px] font-semibold text-indigo-100 whitespace-nowrap">Total Received</th>
                                    <th className="px-2 py-1 text-center text-[10px] font-semibold text-indigo-100 whitespace-nowrap">Transfer To Central</th>
                                    <th className="px-2 py-1 text-center text-[10px] font-semibold text-indigo-100 whitespace-nowrap">Transfer To New Stock</th>
                                    <th className="px-2 py-1 text-center text-[10px] font-semibold text-indigo-100 whitespace-nowrap">Issued For Consumption</th>
                                    <th className="px-2 py-1 text-center text-[10px] font-semibold text-indigo-100 whitespace-nowrap">Total Out</th>
                                    <th className="px-2 py-1 text-center text-[10px] font-semibold text-indigo-100 whitespace-nowrap">Basic Price</th>
                                    <th className="px-2 py-1 text-center text-[10px] font-semibold text-indigo-100 whitespace-nowrap">Amt. Received</th>
                                    <th className="px-2 py-1 text-center text-[10px] font-semibold text-indigo-100 whitespace-nowrap">Amt. Consumed</th>
                                    <th className="px-2 py-1 text-center text-[10px] font-semibold text-indigo-100 whitespace-nowrap">Amt. Transferred</th>
                                    <th className="px-2 py-1 text-center text-[10px] font-semibold text-indigo-100 whitespace-nowrap">Amt. Balance Stock</th>
                                    <th className="px-2 py-1 text-center text-[10px] font-semibold text-indigo-100 whitespace-nowrap">Amt. Lost/Damage</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {rows.map((row, i) => (
                                    <tr key={i} className="hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-colors">
                                        <td className="px-2 py-1 text-xs whitespace-nowrap">
                                            <button onClick={() => openPopup(row.ItemCode)} className="text-emerald-600 dark:text-emerald-400 font-semibold hover:underline">{row.ItemCode}</button>
                                        </td>
                                        <td className="px-2 py-1 text-xs text-gray-800 dark:text-gray-100 w-[180px] max-w-[180px] truncate" title={row.ItemName}>{row.ItemName}</td>
                                        <td className="px-2 py-1 text-xs text-gray-600 dark:text-gray-300 w-[160px] max-w-[160px] truncate" title={row.Specification}>{row.Specification}</td>
                                        <td className="px-2 py-1 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">{row.Units}</td>
                                        <NumCell value={row.OpeningStock} />
                                        <NumCell value={row.rcv_frm_central} /><NumCell value={row.rcv_frm_newstock} /><NumCell value={row.purchased_at_cc} /><NumCell value={row.TotalRecievedAtCC} />
                                        <NumCell value={row.Trans_to_central} /><NumCell value={row.Trans_to_newstock} /><NumCell value={row.Issue_for_cons} /><NumCell value={row.TotalOutFromCC} />
                                        <NumCell value={row.BalanceStockAtCC} />
                                        <NumCell value={row.lostdamaged_rep_accep} />
                                        <NumCell value={row.BasicPrice} /><NumCell value={row.AmountAgainstTotalRecievedCC} /><NumCell value={row.AmtOfConsumedItemsAtCC} />
                                        <NumCell value={row.AmtAgainstTotalTransferedToOtherCC} /><NumCell value={row.AmtOfBalanceStockAtCC} /><NumCell value={row.AmtOfLostDamage} />
                                        <NumCell value={row.ClosingStock} />
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="bg-gray-100 dark:bg-gray-900/50 font-bold">
                                    <td className="px-2 py-1 text-xs" colSpan={5}>TOTAL</td>
                                    <td /><td /><td /><td /><td /><td /><td /><td /><td /><td />
                                    <NumCell value={totals.BasicPrice} /><NumCell value={totals.AmountAgainstTotalRecievedCC} /><NumCell value={totals.AmtOfConsumedItemsAtCC} />
                                    <NumCell value={totals.AmtAgainstTotalTransferedToOtherCC} /><NumCell value={totals.AmtOfBalanceStockAtCC} /><NumCell value={totals.AmtOfLostDamage} />
                                    <td />
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            ) : null}

            {popupItem && (
                <PopupModal itemCode={popupItem} rows={popupData} loading={popupLoading} onClose={closePopup} />
            )}
        </div>
    );
};

export default StockStatusReport;
